<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { BrainCircuit, Save, UserRound } from "lucide-vue-next";
import { v3 } from "@/services/v3";

type ClientProfile = {
  preferredName: string;
  organization: string;
  industry: string;
  brandSummary: string;
  preferences: string[];
  portrait: { overview?: string; creativePreferences?: string[]; deliveryPreferences?: string[]; workflowPreferences?: string[]; dislikes?: string[]; standingConstraints?: string[] };
  memories: Array<{ id: number; value: string; category: string }>;
  memoryCount: number;
  portraitVersion: number;
  portraitUpdatedAt: string;
};

const { t, locale } = useI18n();
const profile = ref<ClientProfile | null>(null);
const preferredName = ref("");
const organization = ref("");
const industry = ref("");
const brandSummary = ref("");
const preferenceText = ref("");
const saving = ref(false);
const notice = ref("");
let refreshTimer: number | undefined;
const summary = computed(() => profile.value
  ? t("profile.summary", { name: profile.value.preferredName, count: profile.value.memoryCount || profile.value.preferences.length })
  : t("profile.loading"));
const portraitItems = computed(() => profile.value ? [
  ...(profile.value.portrait.creativePreferences || []), ...(profile.value.portrait.deliveryPreferences || []),
  ...(profile.value.portrait.workflowPreferences || []), ...(profile.value.portrait.dislikes || []),
  ...(profile.value.portrait.standingConstraints || []),
].filter(Boolean).slice(0, 12) : []);
const portraitUpdated = computed(() => profile.value?.portraitUpdatedAt
  ? new Date(profile.value.portraitUpdatedAt).toLocaleString(locale.value)
  : "");

function sync(value: ClientProfile) {
  profile.value = value;
  preferredName.value = value.preferredName;
  organization.value = value.organization;
  industry.value = value.industry;
  brandSummary.value = value.brandSummary;
  preferenceText.value = value.preferences.join("\n");
}

async function load() {
  const result = await v3.clientProfile();
  sync(result.profile);
}

async function save() {
  saving.value = true;
  notice.value = "";
  try {
    const result = await v3.saveClientProfile({
      preferredName: preferredName.value,
      organization: organization.value,
      industry: industry.value,
      brandSummary: brandSummary.value,
      preferences: preferenceText.value.split("\n").map((item) => item.trim()).filter(Boolean),
    });
    sync(result.profile);
    notice.value = t("profile.saved");
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  void load();
  refreshTimer = window.setInterval(() => { if (!document.hidden && !saving.value) void load(); }, 20_000);
});
onBeforeUnmount(() => { if (refreshTimer) window.clearInterval(refreshTimer); });
</script>

<template>
  <details class="profile-panel">
    <summary>
      <span class="profile-icon"><UserRound :size="18" /></span>
      <span><b>{{ t("profile.title") }}</b><small>{{ summary }}</small></span>
      <BrainCircuit :size="18" />
    </summary>
    <form v-if="profile" @submit.prevent="save">
      <section class="learned-profile">
        <header><b>{{ t("profile.portrait") }}</b><span v-if="profile.portraitVersion">{{ t("profile.portraitVersion", { version: profile.portraitVersion }) }}</span></header>
        <p v-if="profile.portrait.overview">{{ profile.portrait.overview }}</p>
        <p v-else-if="!profile.memories.length" class="muted">{{ t("profile.emptyMemory") }}</p>
        <div v-if="profile.memories.length || portraitItems.length" class="memory-list">
          <span v-for="memory in profile.memories" :key="`memory-${memory.id}`">{{ memory.value }}</span>
          <span v-for="item in portraitItems" :key="`portrait-${item}`">{{ item }}</span>
        </div>
        <small v-if="portraitUpdated">{{ t("profile.updated", { time: portraitUpdated }) }}</small>
      </section>
      <div class="profile-grid">
        <label>{{ t("profile.preferredName") }}<input v-model="preferredName" class="input" maxlength="80" required /></label>
        <label>{{ t("profile.organization") }}<input v-model="organization" class="input" maxlength="160" /></label>
        <label>{{ t("profile.industry") }}<input v-model="industry" class="input" maxlength="120" /></label>
        <label class="wide">{{ t("profile.brandSummary") }}<textarea v-model="brandSummary" class="textarea" maxlength="2000"></textarea></label>
        <label class="wide">{{ t("profile.preferences") }}<textarea v-model="preferenceText" class="textarea" :placeholder="t('profile.preferencePlaceholder')"></textarea></label>
      </div>
      <footer><span>{{ notice }}</span><button class="btn btn--primary" :disabled="saving"><Save :size="16" />{{ saving ? t("common.saving") : t("profile.save") }}</button></footer>
    </form>
  </details>
</template>

<style scoped>
.profile-panel{margin:14px 30px 0;border:1px solid var(--line);background:#fff}.profile-panel>summary{display:grid;grid-template-columns:36px minmax(0,1fr) 24px;align-items:center;gap:10px;min-height:58px;padding:8px 12px;cursor:pointer;list-style:none}.profile-panel>summary::-webkit-details-marker{display:none}.profile-panel>summary>span:nth-child(2){min-width:0}.profile-panel b,.profile-panel small{display:block}.profile-panel small{margin-top:3px;overflow:hidden;color:var(--muted);font-size:11px;text-overflow:ellipsis;white-space:nowrap}.profile-icon{display:grid;width:34px;height:34px;place-items:center;background:#edf5f6;color:var(--brand-dark)}form{padding:14px;border-top:1px solid var(--line)}.profile-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}.profile-grid label{display:grid;gap:5px;color:var(--muted);font-size:12px}.profile-grid .wide{grid-column:1/-1}.textarea{min-height:76px}footer{display:flex;align-items:center;justify-content:flex-end;gap:12px;margin-top:12px}footer span{margin-right:auto;color:var(--success);font-size:12px}.btn{display:inline-flex;align-items:center;gap:6px}@media(max-width:700px){.profile-panel{margin:10px 14px 0}.profile-panel>summary{min-height:54px}.profile-grid{grid-template-columns:1fr}.profile-grid .wide{grid-column:1}form{padding:12px}footer{display:grid}footer .btn{justify-content:center;min-height:42px}}
.learned-profile{display:grid;gap:7px;margin-bottom:14px;padding-bottom:14px;border-bottom:1px solid var(--line)}.learned-profile header{display:flex;align-items:center;justify-content:space-between;gap:10px}.learned-profile header span,.learned-profile small{color:var(--muted);font-size:11px}.learned-profile p{margin:0;font-size:12px;line-height:1.55}.memory-list{display:flex;flex-wrap:wrap;gap:6px}.memory-list span{padding:5px 7px;background:#edf5f6;color:var(--brand-dark);font-size:11px;line-height:1.3}
</style>
